import {Component, inject, OnInit, signal} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {AdminRestaurantStore} from "@features/admin";


@Component({
  selector: 'app-admin-restaurant-form',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, RouterLink],
  templateUrl: 'admin-restaurant-form.component.html',
  styleUrl: 'admin-restaurant-form.component.scss'
})
export class AdminRestaurantFormComponent implements OnInit {
  readonly store = inject(AdminRestaurantStore);
  isEditMode = signal(false);
  restaurantId = signal<number | null>(null);
  mainImagePreview = signal<string | null>(null);
  logoPreview = signal<string | null>(null);
  daysOfWeek = [
    {key: 'monday', label: 'Lundi'},
    {key: 'tuesday', label: 'Mardi'},
    {key: 'wednesday', label: 'Mercredi'},
    {key: 'thursday', label: 'Jeudi'},
    {key: 'friday', label: 'Vendredi'},
    {key: 'saturday', label: 'Samedi'},
    {key: 'sunday', label: 'Dimanche'}
  ];
  private readonly fb = inject(FormBuilder);
  restaurantForm: FormGroup = this.fb.group({
    name: ['', [Validators.required, Validators.minLength(2)]],
    description: [''],
    cuisineType: ['', Validators.required],
    priceRange: ['$$'],
    phone: ['', Validators.required],
    email: ['', Validators.email],
    website: [''],
    status: ['pending'],
    address: this.fb.group({
      street: ['', Validators.required],
      city: ['', Validators.required],
      postalCode: ['', Validators.required],
      country: ['FR']
    }),
    openingHours: this.fb.group({
      monday: this.createDayGroup(),
      tuesday: this.createDayGroup(),
      wednesday: this.createDayGroup(),
      thursday: this.createDayGroup(),
      friday: this.createDayGroup(),
      saturday: this.createDayGroup(),
      sunday: this.createDayGroup(true)
    }),
    deliveryOptions: this.fb.group({
      deliveryRadius: [5],
      deliveryFee: [3.5],
      minOrderAmount: [15],
      freeDeliveryThreshold: [35],
      estimatedDeliveryTime: [45]
    }),
    deliveryEnabled: [true],
    pickupEnabled: [true],
    dineInEnabled: [false],
    owner: this.fb.group({
      firstName: [''],
      lastName: [''],
      email: ['', Validators.email],
      phone: ['']
    })
  });
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.isEditMode.set(true);
      this.restaurantId.set(Number(id));
      this.loadRestaurant(Number(id));
    }
  }

  loadRestaurant(id: number): void {
    // Charger le restaurant pour l'édition
    this.store.loadRestaurantById(id);

    // Dans un cas réel, on subscribe au store pour remplir le formulaire
    // Pour l'instant, on simule avec des données demo
  }

  triggerFileInput(inputName: string): void {
    const input = document.querySelector(`input[type="file"]#${inputName}`) as HTMLInputElement;
    if (input) {
      input.click();
    }
  }

  onMainImageChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      const reader = new FileReader();
      reader.onload = (e) => {
        this.mainImagePreview.set(e.target?.result as string);
      };
      reader.readAsDataURL(input.files[0]);
    }
  }

  onLogoChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      const reader = new FileReader();
      reader.onload = (e) => {
        this.logoPreview.set(e.target?.result as string);
      };
      reader.readAsDataURL(input.files[0]);
    }
  }

  saveRestaurant(): void {
    if (this.restaurantForm.valid) {
      const formValue = this.restaurantForm.value;

      if (this.isEditMode()) {
        this.store.updateRestaurant({
          id: this.restaurantId(),
          ...formValue
        });
      } else {
        this.store.createRestaurant(formValue);
      }

      // Rediriger après sauvegarde
      this.router.navigate(['/admin/restaurants']);
    }
  }

  private createDayGroup(closed: boolean = false): FormGroup {
    return this.fb.group({
      closed: [closed],
      open: ['09:00'],
      close: ['22:00']
    });
  }
}

